

import React, { useState, useRef, useEffect } from 'react';
import { Modal, Box, TextField } from '@mui/material';

import useUIPresentationMode from '../helpers/useUIPresentationMode.js';


const PlacesSearchModal = ({ open, setOpen, onPlaceSelected, mapsLoaded }) => {

  const [searchValue, setSearchValue] = useState('');
  const searchBoxRef = useRef(null);
  const autocompleteRef = useRef(null);


  useEffect(() => {

    let autocomplete;

    // Function to initialize the SearchBox (now called after the Modal is open)
    const initializeAutocomplete = () => {

      if (mapsLoaded && searchBoxRef.current) {

        autocomplete = new window.google.maps.places.Autocomplete(searchBoxRef.current, {
          types: ['geocode'],
          componentRestrictions: { country: 'nz' },
        });

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();

          if (!place.geometry || !place.geometry.location) {
            console.log("Returned place contains no geometry");
            return;
          }

          onPlaceSelected({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });

          // Defer modal close until the next tick. Hopefully helps with the map not reacting to selections.
          setTimeout(() => {
            setOpen(false);
          }, 0);
        });

        autocompleteRef.current = autocomplete;
      }
    };

    // Call initializeSearchBox when the modal opens.
    if (open) {  // Check if open is true to trigger initialization.

      setTimeout(() => {
        if (searchBoxRef.current) {
          searchBoxRef.current.focus();  // ✅ Focus the input
        }
        initializeAutocomplete();
      }, 150); // Delay lets TextField fully render before trying to initialise the google places autocomplete. searchBoxRef.current can still be null.
    }

    return () => {  // Cleanup function

      setSearchValue('');  // Clear input so we have a fresh box next time.

      if (autocompleteRef.current) {  // Make sure you have the autocomplete instance

        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };

  }, [mapsLoaded, onPlaceSelected, open, setOpen]);

  const handleClose = () => {

    if (!searchValue) {
      //console.log("Clicked away from the seach box with no search complete.");
      //alert('Please select a place');  // Uncomment these lines to enforce a location search.
      //return;  // Uncomment these lines to enforce a location search.
    }

    setOpen(false);
  };


  // Scale this mofo depending on screen size.
  const { presentationModeSize } = useUIPresentationMode(); // Get the clean responsive size

  const [scaleFactor, setScaleFactor] = useState(0.9);

  useEffect(() => {
    if (presentationModeSize === 1 || presentationModeSize === 2) {  // Big!
      setScaleFactor(0.85);
    }
    if (presentationModeSize === 3) {  // Not as big.
      setScaleFactor(0.8);
    }
    if (presentationModeSize === 4 || presentationModeSize === 5) {  // Little!
      setScaleFactor(0.7);
    }
  }, [presentationModeSize]);


  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        id="modal-content"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${scaleFactor})`,
          width: 420,
          backgroundColor: 'var(--phbluesuperwashedbackground)',
          borderRadius: '10px',
          padding: '30px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
          p: 4,
          zIndex: 10000,
          color: 'var(--phblue)',
          textAlign: 'center',
        }}
      >
        <h2 style={styles.headerLabel}>Address Search</h2>

        <TextField
          inputRef={searchBoxRef}
          //label="Enter an address"  // We don't want this secondary label/placeholder.
          label=""
          placeholders="Enter an address"
          fullWidth
          variant="outlined"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          InputProps={{
            sx: {
              backgroundColor: '#fff',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 600,
              color: 'var(--phblue)',
              height: '50px',
              padding: '0px 0px 0px 0px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--phblue)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--phblue)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--phblue)',
                borderWidth: '1.5px',
              }
            }
          }}
        />
      </Box>
    </Modal>
  );

};



const styles = {
  headerLabel: {
    fontSize: '22px',
    marginBottom: '10px',
    fontWeight: 700,
    color: 'var(--phblue)',
    padding: '0px 10px 10px 0px'
  }
};


export default PlacesSearchModal;
